import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Tooltip,
  Alert,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@mui/material";
import BurbujasLoader from "../../../components/UIElements/BurbujasLoader";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import useFeedbackModal from "../../../hooks/useFeedbackModal";
import {
  subirNotaDeAutorizacionNuevo,
  reemplazarPdfNotaAutorizacionNuevo,
} from "../../../services/notasDeAutorizacionNuevo.service";

const SUGGESTED_MAX_SIZE_MB = 2;
const INSTITUTIONAL_CYAN = "#009EE3";


const ModalCargarNotaNuevo = ({
  notaId,
  open,
  onClose,
  onSuccess,
  isReemplazo = false,
  userEmail = "",
}) => {
  const { showSuccess, showConfirm } = useFeedbackModal();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isDragOver, setIsDragOver] = useState(false);
  const [lastUploadTime, setLastUploadTime] = useState(() => {
    const saved = localStorage.getItem("lastNotaUploadTime");
    return saved ? parseInt(saved, 10) : null;
  });
  const fileInputRef = useRef(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setFile(null);
      setAlert({ open: false, message: "", severity: "success" });
      setIsDragOver(false);
    }
  }, [open]);

  const checkFileSize = (selectedFile) => {
    const sizeInMB = selectedFile.size / (1024 * 1024);
    if (sizeInMB > SUGGESTED_MAX_SIZE_MB) {
      setAlert({
        open: true,
        message: (
          <span>
            El archivo es demasiado pesado (máximo {SUGGESTED_MAX_SIZE_MB}MB).
            Por favor, comprimilo en{" "}
            <Link
              href="https://www.ilovepdf.com/es/comprimir_pdf"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "inherit", fontWeight: "bold" }}
            >
              iLovePDF
            </Link>{" "}
            para poder subirlo.
          </span>
        ),
        severity: "error",
      });
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === "application/pdf") {
        setFile(selectedFile);
        setAlert({ open: false, message: "", severity: "success" });
        checkFileSize(selectedFile);
      } else {
        setAlert({
          open: true,
          message: "Por favor, seleccione un archivo PDF.",
          severity: "error",
        });
      }
    }
  };

  const executeUpload = async () => {
    setLoading(true);
    setAlert({ open: false, message: "", severity: "success" });

    try {
      let uploadResult = null;
      if (isReemplazo) {
        uploadResult = await reemplazarPdfNotaAutorizacionNuevo(notaId, file);
      } else {
        uploadResult = await subirNotaDeAutorizacionNuevo(notaId, file);
      }
      const destinatarioEmail =
        uploadResult?.destinatario_email ||
        userEmail ||
        "";
      const now = Date.now();
      setLastUploadTime(now);
      localStorage.setItem("lastNotaUploadTime", now.toString());

      handleResetFile(false);
      onSuccess();
      onClose();

      await showSuccess({
        title: isReemplazo
          ? "¡Nota de Autorización Reemplazada Exitosamente!"
          : "¡Nota de Autorización Subida Exitosamente!",
        detail: destinatarioEmail
          ? {
            text: "Se ha enviado una notificación por correo a",
            highlight: destinatarioEmail,
            suffix: "con el detalle del estado y pasos a seguir.",
          }
          : "Se ha enviado una notificación por correo con el detalle del estado y pasos a seguir.",
        confirmText: "Aceptar",
      });
    } catch (error) {
      console.error("Upload error:", error);
      const isLargeFile =
        error.response?.status === 413 ||
        error.message?.includes("413") ||
        error.response?.data?.message?.toLowerCase().includes("large");

      if (isLargeFile) {
        setAlert({
          open: true,
          message: (
            <span>
              El servidor rechazó el archivo por su peso. Por favor, comprimilo
              en{" "}
              <Link
                href="https://www.ilovepdf.com/es/comprimir_pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "inherit", fontWeight: "bold" }}
              >
                iLovePDF
              </Link>{" "}
              e intentá nuevamente.
            </span>
          ),
          severity: "error",
        });
      } else {
        const errorMessage =
          error.response?.data?.message ||
          "Error al subir la nota de autorización.";
        setAlert({ open: true, message: errorMessage, severity: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = async () => {
    if (!file) {
      setAlert({
        open: true,
        message: "Por favor, seleccione un archivo para subir.",
        severity: "warning",
      });
      return;
    }

    if (file.size / (1024 * 1024) > SUGGESTED_MAX_SIZE_MB) {
      setAlert({
        open: true,
        message: `El archivo supera el límite de ${SUGGESTED_MAX_SIZE_MB}MB.`,
        severity: "error",
      });
      return;
    }

    // Check localStorage for recent uploads
    const savedTime = localStorage.getItem("lastNotaUploadTime");
    const effectiveLastTime = savedTime ? Number(savedTime) : lastUploadTime;

    const now = Date.now();
    const threeMinutesInMs = 3 * 60 * 1000;

    if (effectiveLastTime && now - effectiveLastTime < threeMinutesInMs) {
      const isConfirmed = await showConfirm({
        type: "warning",
        title: "Confirmación de Carga",
        description: "Cargaste una nota hace poco menos de 3 minutos.",
        detail: "¿Estás seguro de que querés cargar otra nota de autorización?",
        confirmText: "Sí, subir",
        cancelText: "Cancelar",
      });
      if (!isConfirmed) return;
    }

    executeUpload();
  };

  const handleResetFile = (clearAlert = true) => {
    setFile(null);
    if (clearAlert) {
      setAlert({ open: false, message: "", severity: "success" });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.type === "application/pdf") {
        setFile(droppedFile);
        setAlert({ open: false, message: "", severity: "success" });
        checkFileSize(droppedFile);
      } else {
        setAlert({
          open: true,
          message: "Por favor, seleccione un archivo PDF.",
          severity: "error",
        });
      }
    }
  };

  const isTooLarge = file && file.size / (1024 * 1024) > SUGGESTED_MAX_SIZE_MB;

  return (
    <>
      {/* Main Upload Modal */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
            border: "1px solid #f1f5f9",
            p: { xs: 3, sm: 3.5 },
            m: 2,
            maxWidth: "500px",
          },
        }}
      >
        {/* HeaderSection */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "14px", pr: 1 }}>
            {/* Icono circular de carga */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "#e8f6f8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "#008ba8",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 19h14v2H5v-2zm7-17l7 8h-4.5v6h-5v-6H5l7-8z" />
              </svg>
            </Box>
            {/* Textos del título y subtítulo */}
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: "17px",
                  color: "#1e293b",
                  lineHeight: 1.25,
                }}
              >
                {isReemplazo
                  ? "Reemplazar Nota de Autorización"
                  : "Subir Nota de Autorización"}
              </Typography>
              <Typography
                sx={{
                  color: "#94a3b8",
                  fontSize: "13px",
                  mt: 0.5,
                  lineHeight: 1.35,
                }}
              >
                Adjuntá la nota de autorización para habilitar los cursos.
              </Typography>
            </Box>
          </Box>

          {/* Botón cerrar modal */}
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Cerrar ventana modal"
            sx={{
              color: "#94a3b8",
              p: 0.5,
              mr: -0.5,
              mt: -0.5,
              "&:hover": { color: "#475569", backgroundColor: "transparent" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Divider horizontal */}
        <Box
          sx={{
            borderTop: "1px solid #f1f5f9",
            mb: 2.5,
            mx: { xs: -3, sm: -3.5 },
          }}
        />

        {alert.open && (
          <Alert
            severity={alert.severity}
            sx={{ width: "100%", mb: 2.5, borderRadius: "12px" }}
            onClose={() => setAlert({ ...alert, open: false })}
          >
            {alert.message}
          </Alert>
        )}

        {/* DropzoneSection */}
        <Box sx={{ mb: 2.5 }}>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: "none" }}
            id="file-upload-modal"
          />

          {!file ? (
            <Box
              component="label"
              htmlFor="file-upload-modal"
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 3.5,
                px: 2,
                border: "2px dashed",
                borderColor: isDragOver
                  ? "#008ba8"
                  : alert.open && (alert.severity === "error" || alert.severity === "warning")
                    ? "#ef4444"
                    : "#e2e8f0",
                borderRadius: "16px",
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: isDragOver ? "#f8fafc" : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: alert.open && (alert.severity === "error" || alert.severity === "warning") ? "#ef4444" : "#22d3ee",
                  backgroundColor: "rgba(248, 250, 252, 0.6)",
                  "& .upload-arrow-icon": {
                    color: "#06b6d4",
                  },
                },
              }}
            >
              <Box
                className="upload-arrow-icon"
                sx={{
                  mb: 1,
                  color: isDragOver ? "#008ba8" : "#cbd5e1",
                  transition: "color 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 19h14v2H5v-2zm7-17l7 8h-4.5v6h-5v-6H5l7-8z" />
                </svg>
              </Box>
              <Typography
                sx={{
                  fontSize: "13.5px",
                  color: "#64748b",
                  fontWeight: 500,
                }}
              >
                Arrastrá tu PDF acá o{" "}
                <Box
                  component="span"
                  sx={{
                    color: "#008ca8",
                    fontWeight: 600,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  hacé clic para seleccionar
                </Box>
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 3,
                px: 2,
                border: "2px dashed #008ba8",
                borderRadius: "16px",
                textAlign: "center",
                backgroundColor: "#f0fdfa",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#008ba8",
                  mb: 1.5,
                }}
              >
                ¡Archivo cargado!
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#e6fffa",
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                  maxWidth: "90%",
                  border: "1px solid #99f6e4",
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: "13.5px",
                    fontWeight: 600,
                    color: "#0f766e",
                    mr: 1,
                    maxWidth: "240px",
                  }}
                >
                  {file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleResetFile()}
                  title="Eliminar archivo"
                  sx={{ color: "#ef4444", p: 0.3 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#64748b",
                  mt: 1.5,
                }}
              >
                Hacé clic en el botón de abajo para finalizar el envío.
              </Typography>
            </Box>
          )}
        </Box>

        {/* InformationAlert */}
        <Box
          component="aside"
          sx={{
            backgroundColor: "#ebf6fd",
            borderRadius: "12px",
            p: "14px",
            mb: 2.5,
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          {/* Icono de información circular */}
          <Box
            sx={{
              flexShrink: 0,
              mt: "2px",
              color: "#0091d0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              />
            </svg>
          </Box>
          {/* Texto informativo */}
          <Box sx={{ fontSize: "12px", lineHeight: 1.3 }}>
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                color: "#0091d0",
                letterSpacing: "0.025em",
                display: "block",
                mb: "2px",
                fontSize: "12px",
              }}
            >
              RECUERDA
            </Typography>
            <Typography
              sx={{
                color: "#1390d4",
                fontSize: "12px",
                lineHeight: 1.35,
              }}
            >
              El archivo debe ser PDF y no superar los 2MB para una carga óptima.
            </Typography>
          </Box>
        </Box>

        {/* ActionsFooter */}
        <Box
          component="footer"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Botón principal de Subir */}
          <Tooltip
            title={
              !file
                ? "Seleccione un archivo primero"
                : isTooLarge
                  ? "El archivo es demasiado pesado"
                  : loading
                    ? "Subiendo..."
                    : "Subir la nota de autorización seleccionada"
            }
          >
            <span>
              <Button
                fullWidth
                variant="contained"
                onClick={handleUploadClick}
                disabled={!file || loading || isTooLarge}
                sx={{
                  py: 1.5,
                  backgroundColor: "#008ba8",
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: "14px",
                  letterSpacing: "0.025em",
                  borderRadius: "12px",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  textTransform: "none",
                  transition: "background-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: "#007b95",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                    color: "rgba(0, 0, 0, 0.26)",
                  },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BurbujasLoader small />
                    Subiendo...
                  </Box>
                ) : isReemplazo ? (
                  "REEMPLAZAR"
                ) : (
                  "SUBIR"
                )}
              </Button>
            </span>
          </Tooltip>

          {/* Botón secundario de Cancelar */}
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={loading}
            sx={{
              py: 1.5,
              backgroundColor: "#ffffff",
              color: "#475569",
              fontWeight: 700,
              fontSize: "14px",
              letterSpacing: "0.025em",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              textTransform: "none",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "#f8fafc",
                borderColor: "#cbd5e1",
              },
            }}
          >
            CANCELAR
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default ModalCargarNotaNuevo;
